import { useState } from "react";
import { Menu, ActionIcon } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faFileArrowDown,
} from "@fortawesome/sharp-solid-svg-icons";
import { faClipboardList } from "@fortawesome/sharp-regular-svg-icons";
import { useMutation } from "@apollo/client";
import { format } from "date-fns";
import { gql } from "@/__generated__";
import { ProviderReferralsQuery } from "@/__generated__/graphql";
import styles from "./referral-actions-dropdown.module.scss";

const GET_DOWNLOAD_LINK = gql(`
  mutation DownloadAppealLetterLink($appealId: ID!) {
    downloadAppealLetterLink(appealId: $appealId)
  }
`);

interface ReferralActionsDropdownProps {
  appeal: ProviderReferralsQuery["referrals"]["referrals"][number]["appeal"];
  onUpdateOutcome: () => void;
  onDownloadError?: (error: string) => void;
  canDownload: boolean;
  canUpdateOutcome: boolean;
}

export function ReferralActionsDropdown({
  appeal,
  onUpdateOutcome,
  onDownloadError,
  canDownload,
  canUpdateOutcome,
}: ReferralActionsDropdownProps) {
  const [opened, setOpened] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [getDownloadLink] = useMutation(GET_DOWNLOAD_LINK, {
    fetchPolicy: "network-only",
  });

  const handleDownloadLetter = async () => {
    setIsDownloading(true);
    try {
      const resp = await getDownloadLink({
        variables: { appealId: appeal.appealId },
      });

      if (resp.data?.downloadAppealLetterLink) {
        const pdfResponse = await fetch(
          resp.data.downloadAppealLetterLink as string,
        );

        if (!pdfResponse.ok) {
          throw new Error(
            `Failed to download appeal letter: ${pdfResponse.statusText}`,
          );
        }

        const pdfBlob = await pdfResponse.blob();
        const downloadAnchor = document.createElement("a");
        const downloadAnchorUrl = URL.createObjectURL(pdfBlob);
        let filename = `${format(new Date(), "MM-dd-yyyy")}-Appeal`;
        if (appeal.patient) {
          filename = `${appeal.patient.firstName?.charAt(0)}${appeal.patient.lastName}-${filename}`;
        }
        downloadAnchor.href = downloadAnchorUrl;
        downloadAnchor.download = filename;
        downloadAnchor.target = "_blank";
        downloadAnchor.rel = "noopener noreferrer";

        downloadAnchor.click();

        URL.revokeObjectURL(downloadAnchorUrl);
      } else {
        throw new Error("Failed to get download link for appeal letter");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to download appeal letter";
      onDownloadError?.(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!canUpdateOutcome && !canDownload) {
    return null;
  }

  return (
    <Menu opened={opened} onChange={setOpened} position="bottom-end">
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray" aria-label="Referral actions">
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown className={styles.dropdown}>
        {canUpdateOutcome && (
          <Menu.Item
            onClick={onUpdateOutcome}
            className={styles.menuItem}
            leftSection={
              <FontAwesomeIcon icon={faClipboardList} className={styles.icon} />
            }
          >
            <span className={styles.underlinedText}>Report appeal status</span>
          </Menu.Item>
        )}
        {canDownload && (
          <Menu.Item
            onClick={handleDownloadLetter}
            className={styles.menuItem}
            disabled={isDownloading}
            leftSection={
              <FontAwesomeIcon icon={faFileArrowDown} className={styles.icon} />
            }
          >
            <span className={styles.underlinedText}>
              {isDownloading ? "Downloading..." : "Download appeal letter"}
            </span>
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
